import { useState, useMemo, useEffect } from 'react';
import { QRType, QRPayload, QRDesign, QRHealthReport } from '@/types/qr';
import { encodePayload } from '@/lib/qr/payloads';
import { validatePayload, ValidationError } from '@/lib/qr/validation';
import { evaluateQRHealth } from '@/lib/qr/health';
import { saveHistoryItem } from '@/lib/storage/db';
import { HistoryItem } from '@/types/history';
import { useDebouncedValue } from './useDebouncedValue';

export const DEFAULT_DESIGN: QRDesign = {
  dotsType: 'square',
  dotsColor: '#1d1d1f',
  cornersSquareType: 'square',
  cornersSquareColor: '#1d1d1f',
  cornersDotType: 'square',
  cornersDotColor: '#1d1d1f',
  backgroundColor: '#ffffff',
  isTransparent: false,
  margin: 12,
  errorCorrection: 'H',
  frame: {
    type: 'none',
    text: 'SCAN ME',
    textColor: '#0071e3',
    backgroundColor: '#ffffff'
  }
};

export const INITIAL_PAYLOADS: Record<QRType, QRPayload> = {
  url: { type: 'url', url: 'https://qr.planmaster.in', title: 'QR Studio' },
  text: { type: 'text', text: 'Welcome to QR Studio — Fast, beautiful, and 100% private.' },
  email: { type: 'email', email: 'hello@planmaster.in', subject: 'Inquiry', body: '' },
  phone: { type: 'phone', phone: '+1 555-0199' },
  sms: { type: 'sms', phone: '+1 555-0199', message: 'Hello from QR Studio!' },
  wifi: { type: 'wifi', ssid: 'StudioGuest-5G', password: 'securepassword', encryption: 'WPA', hidden: false },
  vcard: {
    type: 'vcard',
    firstName: 'Alex',
    lastName: 'Morgan',
    organization: 'Planmaster Design',
    title: 'Lead Architect',
    email: 'alex@planmaster.in',
    phone: '+1 555-0199',
    website: 'https://qr.planmaster.in',
    city: 'San Francisco',
    state: 'CA',
    country: 'USA'
  },
  geo: { type: 'geo', latitude: '37.7749', longitude: '-122.4194', query: 'San Francisco, CA' },
  event: {
    type: 'event',
    title: 'Product Keynote 2026',
    location: 'Main Auditorium',
    startDate: new Date().toISOString()
  },
  upi: {
    type: 'upi',
    vpa: 'merchant@upi',
    payeeName: 'QR Studio Store',
    amount: '499.00',
    transactionNote: 'Order #1042',
    currency: 'INR'
  },
  social: { type: 'social', platform: 'github', usernameOrUrl: 'planmaster-dev' },
  app: {
    type: 'app',
    appName: 'QR Studio',
    fallbackUrl: 'https://qr.planmaster.in'
  },
  crypto: { type: 'crypto', coin: 'BTC', address: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa', amount: '0.01' },
  custom: { type: 'custom', content: 'custom-uri://action?id=8831' }
};

export function useQRGenerator() {
  const [selectedType, setSelectedType] = useState<QRType>('url');
  const [payloads, setPayloads] = useState<Record<QRType, QRPayload>>(INITIAL_PAYLOADS);
  const [design, setDesign] = useState<QRDesign>(DEFAULT_DESIGN);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [currentId, setCurrentId] = useState<string>(() => `qr-${Date.now()}`);

  const activePayload = payloads[selectedType];

  // Encode active payload into QR-compatible raw text
  const rawText = useMemo(() => {
    return encodePayload(activePayload);
  }, [activePayload]);

  // Field validation
  const validationErrors = useMemo<ValidationError[]>(() => {
    return validatePayload(activePayload);
  }, [activePayload]);

  // Health and scanability diagnostics (debounced so typing doesn't thrash the analysis)
  const debouncedRawText = useDebouncedValue(rawText, 180);
  const debouncedDesign = useDebouncedValue(design, 180);
  const healthReport = useMemo<QRHealthReport>(() => {
    return evaluateQRHealth(debouncedRawText, debouncedDesign);
  }, [debouncedRawText, debouncedDesign]);

  // Switch active QR payload type — starts a new history slot so editing a
  // different type never silently overwrites the previous type's draft.
  const switchType = (type: QRType) => {
    setSelectedType(type);
    setCurrentId(`qr-${Date.now()}`);
  };

  // Update fields of the active payload
  const updatePayload = (updated: QRPayload) => {
    setPayloads((prev) => ({
      ...prev,
      [updated.type]: updated
    }));
  };

  // Load an existing history or template item into the generator
  const loadItem = (item: HistoryItem) => {
    setCurrentId(item.id);
    setSelectedType(item.type);
    setPayloads((prev) => ({
      ...prev,
      [item.type]: item.payload
    }));
    setDesign(item.design);
    setIsFavorite(Boolean(item.isFavorite));
  };

  // Toggle favorite and auto-persist to IndexedDB
  const toggleFavorite = async () => {
    const nextFavorite = !isFavorite;
    setIsFavorite(nextFavorite);

    const historyItem: HistoryItem = {
      id: currentId,
      title: activePayload.type === 'url' ? activePayload.title || activePayload.url : `${activePayload.type.toUpperCase()} QR`,
      type: selectedType,
      payload: activePayload,
      rawText,
      design,
      isFavorite: nextFavorite,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    try {
      await saveHistoryItem(historyItem);
    } catch (err) {
      console.warn('Failed to update favorite item in storage:', err);
    }
  };

  // Auto-save history item after debounced input
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!rawText.trim() || validationErrors.length > 0) return;

      const historyItem: HistoryItem = {
        id: currentId,
        title:
          selectedType === 'url'
            ? (activePayload as any).title || (activePayload as any).url
            : `${selectedType.toUpperCase()} QR`,
        type: selectedType,
        payload: activePayload,
        rawText,
        design,
        isFavorite,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      try {
        await saveHistoryItem(historyItem);
      } catch (err) {
        console.warn('Auto-save history failed:', err);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [rawText, design, selectedType, currentId, isFavorite, validationErrors.length, activePayload]);

  return {
    selectedType,
    switchType,
    activePayload,
    updatePayload,
    design,
    setDesign,
    rawText,
    validationErrors,
    healthReport,
    isFavorite,
    toggleFavorite,
    loadItem
  };
}
